import type { Key } from 'react';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';

import {
  type CalendarDateTime,
  getLocalTimeZone,
  Time,
  toZoned,
} from '@internationalized/date';
import { cva } from 'cva';
import type { DateValue } from 'react-aria-components/Calendar';
import {
  DateSegment,
  DateInput as RACDateInput,
  DatePicker as RACDatePicker,
  type DatePickerProps as RACDatePickerProps,
} from 'react-aria-components/DatePicker';
import { Dialog } from 'react-aria-components/Dialog';
import type { ValidationResult } from 'react-aria-components/FieldError';
import { Group } from 'react-aria-components/Group';

import { CALENDAR_BUTTON_SIZES } from '../../utils/calendar_button_size';
import { cx } from '../../utils/cx';
import {
  applyTimeToDatetime,
  createTimeFromInputs,
  DEFAULT_TIME,
  ensureDateTime,
  formatHourForDisplay,
  getTimeInputsFromDatetime,
  LEGACY_TIME_FORMAT_STORAGE_KEY,
  PLACEHOLDER_DATETIME,
  resolveHoursInput,
  resolveOpenTimeFormat,
  TIME_FORMAT_STORAGE_KEY,
  to24HourFromDisplay,
} from '../../utils/datetime_draft';
import {
  resolveInitialTimeFormat,
  setPersistedTimeFormat,
} from '../../utils/persisted_time_format';
import { Button } from '../button/button';
import { Calendar } from '../calendars/calendar';
import { ClearButton } from '../clear_button/clear_button';
import {
  applyDateSeparator,
  useDateFormat,
} from '../date_format_context/date_format_context';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';
import { Icon } from '../icon/icon';
import { Popover } from '../popover/popover';
import { Text } from '../text/text';
import { type TimeFormatType, TimePicker } from '../time_picker/time_picker';

import styles from './datetime_field.module.css';

export const datetimeFieldVariants = cva({
  base: styles.datetime_field,
  variants: {
    size: { s: styles.size_s, m: styles.size_m, l: styles.size_l },
    readOnly: { true: styles.read_only },
  },
  defaultVariants: { size: 'm' },
});

export type DatetimeFieldProps<T extends DateValue> = RACDatePickerProps<T> & {
  label: string;
  size?: 's' | 'm' | 'l';
  hideLabel?: boolean;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isRequired?: boolean;
  showSeconds?: boolean;
  showTimeZone?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  isFieldClickable?: boolean;
  /** Seeds the initial 12h/24h choice; falls back to the persisted preference. */
  hourCycle?: 12 | 24;
};

export const DatetimeField = forwardRef<
  HTMLDivElement,
  DatetimeFieldProps<DateValue>
>(
  (
    {
      className,
      description,
      errorMessage,
      hideLabel,
      isRequired,
      label,
      size = 'm',
      showSeconds = true,
      showTimeZone,
      isClearable,
      placeholder,
      isFieldClickable,
      ...rest
    },
    ref,
  ) => {
    const {
      onChange,
      onOpenChange,
      value,
      defaultValue,
      placeholderValue,
      minValue,
      maxValue,
      hourCycle: hourCycleProp,
      ...restProps
    } = rest;

    const classes = datetimeFieldVariants({
      size,
      readOnly: restProps.isReadOnly,
      className,
    });

    const { separator } = useDateFormat();

    const { button: buttonSize, icon: calendarIconSize } =
      CALENDAR_BUTTON_SIZES[size];

    const granularity = showSeconds ? 'second' : 'minute';

    // Support controlled or uncontrolled usage:
    const [uncontrolledValue, setUncontrolledValue] =
      useState<CalendarDateTime | null>(
        () => ensureDateTime(defaultValue, DEFAULT_TIME) ?? null,
      );

    const committedValue = useMemo(() => {
      // If controlled, normalize incoming value
      if (value !== undefined) {
        return ensureDateTime(value, DEFAULT_TIME);
      }
      // Uncontrolled internal state
      return uncontrolledValue;
    }, [value, uncontrolledValue]);

    const setCommittedValue = useCallback(
      (next: CalendarDateTime | null) => {
        if (value !== undefined) {
          // controlled: notify only
          onChange?.(next);
        } else {
          // uncontrolled: update internal + notify
          setUncontrolledValue(next);
          onChange?.(next);
        }
      },
      [onChange, value],
    );

    const showPlaceholder = Boolean(placeholder && !committedValue);
    const pickerValue =
      showTimeZone && committedValue
        ? toZoned(committedValue, getLocalTimeZone())
        : committedValue;
    const calendarButtonRef = useRef<HTMLButtonElement>(null);

    const canClickField = Boolean(
      isFieldClickable && !restProps.isReadOnly && !restProps.isDisabled,
    );

    const canClear = Boolean(
      isClearable &&
        committedValue &&
        !restProps.isReadOnly &&
        !restProps.isDisabled,
    );

    const placeholderDatetime =
      ensureDateTime(placeholderValue, new Time(0, 0, 0)) ??
      PLACEHOLDER_DATETIME;

    const [isOpen, setIsOpen] = useState(false);
    const [draftValue, setDraftValue] = useState<CalendarDateTime>(() => {
      return committedValue ?? placeholderDatetime;
    });

    const [appliedFormat, setAppliedFormat] = useState<TimeFormatType>(() =>
      resolveInitialTimeFormat({
        hourCycle: hourCycleProp,
        storageKey: TIME_FORMAT_STORAGE_KEY,
        legacyStorageKey: LEGACY_TIME_FORMAT_STORAGE_KEY,
      }),
    );
    const [draftFormat, setDraftFormat] =
      useState<TimeFormatType>(appliedFormat);
    // Once a value exists, follow the applied choice (which the user can change
    // in the picker); use the prop only to seed the empty initial state.
    let hourCycle = hourCycleProp;
    if (committedValue) {
      hourCycle = appliedFormat === '24h' ? 24 : 12;
    }

    const [hours, setHours] = useState('12');
    const [minutes, setMinutes] = useState('00');
    const [seconds, setSeconds] = useState('00');

    const handleFormatChange = useCallback(
      (nextFormat: TimeFormatType) => {
        const currentHourIn24Format = to24HourFromDisplay(hours, draftFormat);

        setDraftFormat(nextFormat);
        setHours(formatHourForDisplay(currentHourIn24Format, nextFormat));
      },
      [draftFormat, hours],
    );

    const syncTimeInputsFromDraft = useCallback(
      (dateTime: CalendarDateTime) => {
        const timeInputs = getTimeInputsFromDatetime(dateTime, draftFormat);

        setHours(timeInputs.hours);
        setMinutes(timeInputs.minutes);
        setSeconds(timeInputs.seconds);
      },
      [draftFormat],
    );

    const createTimeFromDraftInputs = useCallback(
      () =>
        createTimeFromInputs(
          { hours, minutes, seconds: showSeconds ? seconds : '00' },
          draftFormat,
        ),
      [draftFormat, hours, minutes, seconds, showSeconds],
    );

    // normalize min/max to datetime when provided
    const normalizedMinValue = useMemo(
      () => ensureDateTime(minValue, new Time(0, 0, 0)) ?? undefined,
      [minValue],
    );
    const normalizedMaxValue = useMemo(
      () => ensureDateTime(maxValue, new Time(23, 59, 59)) ?? undefined,
      [maxValue],
    );

    const openPopover = useCallback(() => {
      const base = committedValue ?? placeholderDatetime;
      const openFormat = resolveOpenTimeFormat(appliedFormat, base);
      const timeInputs = getTimeInputsFromDatetime(base, openFormat);

      // snapshot committed -> draft
      setDraftValue(base);
      setDraftFormat(openFormat);
      setHours(timeInputs.hours);
      setMinutes(timeInputs.minutes);
      setSeconds(timeInputs.seconds);

      setIsOpen(true);
      onOpenChange?.(true);
    }, [appliedFormat, committedValue, onOpenChange, placeholderDatetime]);

    const closePopoverRevert = useCallback(() => {
      // discard draft changes
      setIsOpen(false);
      onOpenChange?.(false);
    }, [onOpenChange]);

    const applyAndClose = useCallback(() => {
      const next = applyTimeToDatetime(draftValue, createTimeFromDraftInputs());

      setCommittedValue(next);
      setAppliedFormat(draftFormat);
      setPersistedTimeFormat(TIME_FORMAT_STORAGE_KEY, draftFormat);
      setIsOpen(false);
      onOpenChange?.(false);
    }, [
      createTimeFromDraftInputs,
      draftFormat,
      draftValue,
      onOpenChange,
      setCommittedValue,
    ]);

    return (
      <RACDatePicker
        ref={ref}
        isRequired={isRequired}
        aria-label={hideLabel ? label : undefined}
        className={classes}
        isReadOnly={restProps.isReadOnly}
        {...restProps}
        granularity={granularity}
        hourCycle={hourCycle}
        placeholderValue={
          showTimeZone
            ? toZoned(placeholderDatetime, getLocalTimeZone())
            : placeholderDatetime
        }
        shouldCloseOnSelect={false}
        value={isClearable ? pickerValue : (pickerValue ?? undefined)}
        minValue={normalizedMinValue}
        maxValue={normalizedMaxValue}
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (open) {
            openPopover();
          } else {
            closePopoverRevert();
          }
        }}
      >
        {(render) => {
          const { isInvalid } = render;

          return (
            <>
              {!hideLabel && (
                <FieldLabel isRequired={isRequired}>{label}</FieldLabel>
              )}

              <Group
                className={cx([
                  styles.input_group,
                  {
                    [styles.has_placeholder]: showPlaceholder,
                    [styles.opens_popover_on_click]: canClickField,
                  },
                ])}
                onClick={canClickField ? openPopover : undefined}
              >
                {showPlaceholder ? (
                  <span className={styles.placeholder} aria-hidden>
                    {placeholder}
                  </span>
                ) : null}
                <RACDateInput className={styles.date_input}>
                  {(segment) => (
                    <DateSegment
                      segment={applyDateSeparator(segment, separator)}
                      className={styles.segment}
                    />
                  )}
                </RACDateInput>

                {canClear ? (
                  <ClearButton
                    slot={null}
                    size={buttonSize}
                    iconSize={calendarIconSize}
                    onPress={() => {
                      setCommittedValue(null);
                      calendarButtonRef.current?.focus();
                    }}
                  />
                ) : null}

                <Button
                  ref={calendarButtonRef}
                  size={buttonSize}
                  variant="tertiary"
                  className={styles.calendar_button}
                  icon={
                    <Icon name="calendar_datetime" size={calendarIconSize} />
                  }
                  onPress={openPopover}
                />
              </Group>

              {description && !isInvalid && (
                <Text slot="description">{description}</Text>
              )}

              <FieldError>
                {typeof errorMessage === 'function'
                  ? (validation: ValidationResult) => errorMessage(validation)
                  : errorMessage}
              </FieldError>

              <Popover className={styles.popover} placement="top" offset={8}>
                <Dialog className={styles.dialog}>
                  <div className={styles.popover_stack}>
                    <Calendar<CalendarDateTime>
                      value={draftValue}
                      onChange={(nextValue) => {
                        // ensure date-time (calendar may emit date-only)
                        const next =
                          ensureDateTime(nextValue, DEFAULT_TIME) ?? draftValue;

                        // preserve current draft time fields when date changes
                        const preserved = applyTimeToDatetime(
                          next,
                          createTimeFromDraftInputs(),
                        );

                        setDraftValue(preserved);
                        // keep the time inputs synced
                        syncTimeInputsFromDraft(preserved);
                      }}
                    />

                    <div className={styles.stack_divider} />

                    <div className={styles.time_column}>
                      <TimePicker
                        format={draftFormat}
                        onFormatChange={handleFormatChange}
                        hours={hours}
                        minutes={minutes}
                        onHoursChange={(key: Key | null) => {
                          setHours(resolveHoursInput(key, draftFormat, hours));
                        }}
                        onMinutesChange={(key: Key | null) => {
                          setMinutes(String(key ?? '00'));
                        }}
                        showSeconds={showSeconds}
                        seconds={seconds}
                        onSecondsChange={(key: Key | null) => {
                          setSeconds(String(key ?? '00'));
                        }}
                      />

                      <div className={styles.stack_divider} />

                      <div className={styles.apply_row}>
                        <Button size="m" onClick={applyAndClose}>
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dialog>
              </Popover>
            </>
          );
        }}
      </RACDatePicker>
    );
  },
);
